<template>
  <div class="markdown-editor" :class="editorClasses">
    <!-- Toolbar -->
    <v-toolbar dense flat class="markdown-editor__toolbar" v-if="!readonly">
      <v-btn-toggle dense multiple :value="activeMarks" class="mr-2">
        <v-btn
          small
          :value="'bold'"
          @click="
            editor
              .chain()
              .focus()
              .toggleBold()
              .run()
          "
        >
          <v-icon small>mdi-format-bold</v-icon>
        </v-btn>
        <v-btn
          small
          :value="'italic'"
          @click="
            editor
              .chain()
              .focus()
              .toggleItalic()
              .run()
          "
        >
          <v-icon small>mdi-format-italic</v-icon>
        </v-btn>
        <v-btn
          small
          :value="'strike'"
          @click="
            editor
              .chain()
              .focus()
              .toggleStrike()
              .run()
          "
        >
          <v-icon small>mdi-format-strikethrough</v-icon>
        </v-btn>
      </v-btn-toggle>

      <v-btn-toggle dense :value="activeBlock" class="mr-2">
        <v-btn
          small
          value="paragraph"
          @click="
            editor
              .chain()
              .focus()
              .setParagraph()
              .run()
          "
        >
          <v-icon small>mdi-format-paragraph</v-icon>
        </v-btn>
        <v-btn
          small
          value="heading1"
          @click="
            editor
              .chain()
              .focus()
              .toggleHeading({ level: 1 })
              .run()
          "
        >
          <v-icon small>mdi-format-header-1</v-icon>
        </v-btn>
        <v-btn
          small
          value="heading2"
          @click="
            editor
              .chain()
              .focus()
              .toggleHeading({ level: 2 })
              .run()
          "
        >
          <v-icon small>mdi-format-header-2</v-icon>
        </v-btn>
        <v-btn
          small
          value="heading3"
          @click="
            editor
              .chain()
              .focus()
              .toggleHeading({ level: 3 })
              .run()
          "
        >
          <v-icon small>mdi-format-header-3</v-icon>
        </v-btn>
      </v-btn-toggle>

      <v-btn-toggle dense :value="activeList">
        <v-btn
          small
          value="bulletList"
          @click="
            editor
              .chain()
              .focus()
              .toggleBulletList()
              .run()
          "
        >
          <v-icon small>mdi-format-list-bulleted</v-icon>
        </v-btn>
        <v-btn
          small
          value="orderedList"
          @click="
            editor
              .chain()
              .focus()
              .toggleOrderedList()
              .run()
          "
        >
          <v-icon small>mdi-format-list-numbered</v-icon>
        </v-btn>
      </v-btn-toggle>
    </v-toolbar>

    <!-- Editor Content -->
    <editor-content :editor="editor" class="markdown-editor__content" />

    <!-- Error Message -->
    <div v-if="hasError" class="markdown-editor__error">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "tiptap-markdown";

export default {
  name: "MarkdownEditor",

  components: {
    EditorContent,
  },

  inject: {
    form: { default: null },
  },

  props: {
    value: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      editor: null,
      isFocused: false,
      errorMessage: "",
      hasBeenValidated: false,
    };
  },

  computed: {
    hasContent() {
      return this.value && this.value.trim().length > 0;
    },
    hasError() {
      return this.errorMessage !== "";
    },
    // For Vuetify form integration - determines when validation should run
    shouldValidate() {
      return this.hasBeenValidated;
    },
    editorClasses() {
      return {
        "markdown-editor--focused": this.isFocused,
        "markdown-editor--error": this.hasError,
      };
    },
    activeMarks() {
      if (!this.editor) return [];
      const marks = [];
      if (this.editor.isActive("bold")) marks.push("bold");
      if (this.editor.isActive("italic")) marks.push("italic");
      if (this.editor.isActive("strike")) marks.push("strike");
      return marks;
    },
    activeBlock() {
      if (!this.editor) return "paragraph";
      if (this.editor.isActive("heading", { level: 1 })) return "heading1";
      if (this.editor.isActive("heading", { level: 2 })) return "heading2";
      if (this.editor.isActive("heading", { level: 3 })) return "heading3";
      return "paragraph";
    },
    activeList() {
      if (!this.editor) return null;
      if (this.editor.isActive("bulletList")) return "bulletList";
      if (this.editor.isActive("orderedList")) return "orderedList";
      return null;
    },
  },

  watch: {
    value(newValue) {
      if (!this.editor) return;
      const currentMarkdown = this.editor.storage.markdown.getMarkdown();
      if (currentMarkdown !== newValue) {
        this.editor.commands.setContent(newValue || "", false);
      }
      // Re-validate only if already validated, to update error state on value changes
      if (this.hasBeenValidated) {
        this._revalidate();
      }
    },
  },

  methods: {
    // Internal method for re-validation (doesn't set hasBeenValidated)
    _revalidate() {
      for (const rule of this.rules) {
        const result = rule(this.value);
        if (result !== true) {
          this.errorMessage = result || "Invalid";
          return false;
        }
      }
      this.errorMessage = "";
      return true;
    },
    validate() {
      // Mark as validated when validate() is called (from form.validate() or explicitly)
      // This ensures errors are shown when user attempts to submit
      this.hasBeenValidated = true;

      return this._revalidate();
    },
    reset() {
      this.hasBeenValidated = false;
      this.errorMessage = "";
    },
    resetValidation() {
      this.hasBeenValidated = false;
      this.errorMessage = "";
    },
  },

  mounted() {
    // Register with parent v-form if available
    if (this.form) {
      this.form.register(this);
    }

    this.editor = new Editor({
      content: this.value || "",
      extensions: [
        StarterKit,
        Markdown.configure({
          html: false,
          tightLists: true,
          bulletListMarker: "-",
        }),
      ],
      editable: !this.readonly,
      autofocus: this.autofocus,
      onUpdate: () => {
        const markdown = this.editor.storage.markdown.getMarkdown();
        this.$emit("input", markdown);
      },
      onFocus: () => {
        this.isFocused = true;
      },
      onBlur: ({ event }) => {
        // Check if focus is moving to another element within the component
        const relatedTarget = event?.relatedTarget;
        if (relatedTarget && this.$el.contains(relatedTarget)) {
          // Focus is still within the component (e.g., toolbar button), don't emit blur
          return;
        }
        this.isFocused = false;
        this.$emit("blur");
        this.$emit("change", this.editor.storage.markdown.getMarkdown());
      },
    });
  },

  beforeDestroy() {
    // Unregister from parent v-form
    if (this.form) {
      this.form.unregister(this);
    }

    if (this.editor) {
      this.editor.destroy();
    }
  },
};
</script>

<style lang="scss">
.markdown-editor {
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  transition: border-color 0.2s;
  overflow: clip;

  &--focused {
    border-color: rgba(0, 0, 0, 0.2);
  }

  &--error {
    border-color: var(--v-error-base, #ff5252);
  }

  &__toolbar {
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    background: #fafafa !important;
  }

  &__content {
    padding: 12px;
    min-height: 100px;

    .ProseMirror {
      outline: none;
      min-height: 80px;

      p {
        margin: 0.5em 0;
      }

      ul,
      ol {
        padding-left: 1.5em;
      }

      h1 {
        font-size: 1.5em;
        font-weight: 600;
        margin: 0.5em 0;
      }

      h2 {
        font-size: 1.3em;
        font-weight: 600;
        margin: 0.5em 0;
      }

      h3 {
        font-size: 1.17em;
        font-weight: 600;
        margin: 0.5em 0;
      }

      &:first-child {
        margin-top: 0;
      }
    }
  }

  &__label {
    position: absolute;
    top: -8px;
    left: 8px;
    background: white;
    padding: 0 4px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
    transition: all 0.2s;

    &--active {
      color: var(--v-primary-base);
    }
  }

  &__error {
    color: var(--v-error-base, #ff5252);
    font-size: 12px;
    padding: 4px 12px 8px;
  }
}
</style>
